<script setup lang="ts">
    import { registerModal } from '../../composables/useModalRegistry';
    import { onClickOutside, useDebounceFn, useFocusWithin, useMagicKeys } from '@vueuse/core';

    const {
        isStoryBlokModal = false,
        noPadding = false,
        modalId = useId(),
        controller,
        wide = false
    } = defineProps<{
        isStoryBlokModal?: boolean;
        noPadding?: boolean;
        modalId?: string;
        controller: ReturnType<typeof useModal>;
        wide?: boolean;
    }>();

    defineOptions({
        inheritAttrs: false
    });

    const attrs = useAttrs();

    const modalContentElement = ref<HTMLElement | null>(null);
    const pointerDownInsideModal = ref(false);
    const setPointerDownInsideModalDebounced = useDebounceFn((value: boolean) => {
        pointerDownInsideModal.value = value;
    }, 100);

    const { allowMount } = registerModal(modalId);

    const { isOpen, close } = controller;

    const { clampBody, releaseBody } = useFixedBody();

    const { escape } = useMagicKeys();

    watch(
        () => escape?.value,
        () => {
            if (isOpen.value) {
                close();
            }
        }
    );

    watch([isOpen], async ([open]) => {
        if (open) {
            await nextTick();
            clampBody();
        } else {
            releaseBody();
        }
    });

    onUnmounted(() => {
        if (isOpen.value) {
            releaseBody();
        }
    });

    onClickOutside(modalContentElement, () => {
        if (pointerDownInsideModal.value) return;

        if (isOpen.value) {
            close();
        }
    });

    const { focused } = useFocusWithin(modalContentElement);
    watch(focused, (isFocused) => {
        if (pointerDownInsideModal.value) return;
        if (!document.hasFocus()) return;

        if (!isFocused && isOpen.value) {
            close();
        }
    });
</script>

<template>
    <Teleport
        v-if="allowMount"
        to="body"
    >
        <Transition
            enter-active-class="transition ease-out duration-200 transform"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-200 transform"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="isOpen"
                class="modal-wrapper"
                :class="[{ '--wide': isStoryBlokModal || wide }, attrs?.class]"
            >
                <div class="modal">
                    <Transition
                        enter-active-class="transition ease-out duration-500 transform "
                        enter-from-class="opacity-0 translate-y-5"
                        enter-to-class="opacity-100 translate-y-0"
                        leave-active-class="ease-in duration-200"
                        leave-from-class="opacity-100 translate-y-0"
                        leave-to-class="opacity-0 translate-y-5 translate-y-0"
                    >
                        <dialog
                            v-if="isOpen"
                            id="modal-content"
                            ref="modalContentElement"
                            open
                            class="content"
                            :class="{ '--no-padding': noPadding }"
                            aria-modal="true"
                            :aria-labelledby="isStoryBlokModal ? undefined : 'modal-headline'"
                            @pointerdown="pointerDownInsideModal = true"
                            @pointerup="setPointerDownInsideModalDebounced(false)"
                            @pointerleave="setPointerDownInsideModalDebounced(false)"
                            @pointercancel="setPointerDownInsideModalDebounced(false)"
                        >
                            <div class="content-scroll"><slot></slot></div>

                            <slot name="outside-scroll"></slot>
                        </dialog>
                    </Transition>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<style scoped lang="scss">
    @import '~/assets/scss/basics/shared-modal.scss';
</style>
